import { type Currency, RecordType, Role } from '@kanbu/schema/enums';
import { formatUtils } from '@kanbu/shared';
import { useLingui } from '@lingui/react/macro';
import { Table } from '@utima/ui';

import { Card } from '@/components/card/Card';
import { Protected } from '@/components/protected/Protected';
import { useEnumTranslations } from '@/hooks';
import { trpc } from '@/services/trpc';

interface OrganizationUsageReportProps {
  organizationId: string;
  currency: Currency;
}

export function OrganizationUsageReport({
  organizationId,
  currency,
}: OrganizationUsageReportProps) {
  const { t } = useLingui();
  const enumTranslations = useEnumTranslations();
  const { data, isLoading, error } = trpc.organizations.usageReport.useQuery({
    id: organizationId,
  });

  if (isLoading) {
    return <div className='mt-4 p-4'>Loading usage report...</div>;
  }

  if (error) {
    return (
      <div className='mt-4 p-4 text-danger'>
        Failed to load usage report: {error.message}
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <Protected roles={[Role.SuperAdmin]}>
      {data.unpricedCount > 0 && (
        <div className='mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-800 dark:text-amber-200'>
          {t`${data.unpricedCount} usage record(s) have no catalog price (cost recorded as zero). Add pricing before treating these as free.`}
        </div>
      )}
      {data.months.map(month => (
        <Card key={month.month} className='mb-4'>
          <div className='p-2'>
            <h3 className='mb-4 flex items-center justify-between'>
              <span className='text-xl font-bold text-foreground'>
                {new Date(month.month).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                })}
              </span>
              <span className='text-xl font-bold text-emerald-600'>
                {formatUtils.credits(month.totalCost, currency)}
              </span>
            </h3>

            {month.byCategory.map(category => (
              <div key={category.category} className='mb-4 last:mb-0'>
                <h4 className='mb-3 flex items-center justify-between'>
                  <span className='text-lg font-semibold text-foreground/90'>
                    {enumTranslations.recordCategory[category.category]}
                  </span>
                  <span className='text-base font-semibold text-emerald-600/90'>
                    {formatUtils.credits(category.totalCost, currency)}
                  </span>
                </h4>

                {category.byType.map(typeStats => (
                  <div key={typeStats.type} className='mb-3 last:mb-2'>
                    <div className='mb-3 flex items-center justify-between'>
                      <span className='text-sm font-medium text-foreground/80'>
                        {typeStats.type}
                      </span>
                      <span className='text-sm font-medium text-emerald-600/80'>
                        {formatUtils.credits(typeStats.totalCost, currency)}
                      </span>
                    </div>

                    <div className='rounded-lg backdrop-blur-sm'>
                      <Table.Root>
                        <Table.Head>
                          <Table.Row className='border-b border-border/50 hover:bg-transparent'>
                            <Table.HCol className='w-[140px] text-xs font-medium text-muted-fg'>
                              {t`Provider`}
                            </Table.HCol>
                            <Table.HCol className='w-[140px] text-xs font-medium text-muted-fg'>
                              {t`Model`}
                            </Table.HCol>
                            <Table.HCol className='text-right text-xs font-medium text-muted-fg'>
                              {t`Operations`}
                            </Table.HCol>
                            {typeStats.type === RecordType.Llm && (
                              <>
                                <Table.HCol className='text-right text-xs font-medium text-muted-fg'>
                                  {t`Input tokens`}
                                </Table.HCol>
                                <Table.HCol className='text-right text-xs font-medium text-muted-fg'>
                                  {t`Output tokens`}
                                </Table.HCol>
                                <Table.HCol className='text-right text-xs font-medium text-muted-fg'>
                                  {t`Total tokens`}
                                </Table.HCol>
                                <Table.HCol className='text-right text-xs font-medium text-muted-fg'>
                                  {t`Input cost`}
                                </Table.HCol>
                                <Table.HCol className='text-right text-xs font-medium text-muted-fg'>
                                  {t`Output cost`}
                                </Table.HCol>
                              </>
                            )}
                            {typeStats.type !== RecordType.Llm && (
                              <Table.HCol className='text-right text-xs font-medium text-muted-fg'>
                                {t`Units`}
                              </Table.HCol>
                            )}
                            <Table.HCol className='w-[120px] text-right text-xs font-medium text-muted-fg'>
                              {t`Total cost`}
                            </Table.HCol>
                          </Table.Row>
                        </Table.Head>
                        <Table.Body>
                          {typeStats.byModel.map((modelStats, idx) => (
                            <Table.Row
                              key={`${modelStats.provider}-${modelStats.model}-${idx}`}
                              className='transition-colors hover:bg-muted/30'
                            >
                              <Table.Col className='text-sm font-medium text-foreground/90'>
                                {modelStats.provider}
                              </Table.Col>
                              <Table.Col className='font-mono text-xs text-foreground/80'>
                                {modelStats.model}
                              </Table.Col>
                              <Table.Col className='text-right text-sm tabular-nums text-foreground/80'>
                                {formatUtils.number(modelStats.count)}
                              </Table.Col>
                              {typeStats.type === RecordType.Llm && (
                                <>
                                  <Table.Col className='text-right text-sm tabular-nums text-foreground/80'>
                                    {formatUtils.number(
                                      modelStats.inputTokens || 0,
                                    )}
                                  </Table.Col>
                                  <Table.Col className='text-right text-sm tabular-nums text-foreground/80'>
                                    {formatUtils.number(
                                      modelStats.outputTokens || 0,
                                    )}
                                  </Table.Col>
                                  <Table.Col className='text-right text-sm tabular-nums text-foreground/80'>
                                    {formatUtils.number(
                                      (modelStats.inputTokens || 0) +
                                        (modelStats.outputTokens || 0),
                                    )}
                                  </Table.Col>
                                  <Table.Col className='text-right text-sm tabular-nums text-emerald-600/80 dark:text-emerald-400/80'>
                                    {formatUtils.credits(
                                      modelStats.inputCost || 0,
                                      currency,
                                    )}
                                  </Table.Col>
                                  <Table.Col className='text-right text-sm tabular-nums text-emerald-600/80 dark:text-emerald-400/80'>
                                    {formatUtils.credits(
                                      modelStats.outputCost || 0,
                                      currency,
                                    )}
                                  </Table.Col>
                                </>
                              )}
                              {typeStats.type !== RecordType.Llm && (
                                <Table.Col className='text-right text-sm tabular-nums text-foreground/80'>
                                  {formatUtils.number(modelStats.totalUnits)}
                                </Table.Col>
                              )}
                              <Table.Col className='text-right text-sm font-semibold tabular-nums text-emerald-600 dark:text-emerald-400'>
                                {formatUtils.credits(
                                  modelStats.totalCost,
                                  currency,
                                )}
                              </Table.Col>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table.Root>
                    </div>
                  </div>
                ))}
              </div>
            ))}

            <div className='mt-4 flex items-center justify-end gap-2 text-xs'>
              <span className='font-medium text-muted-fg'>
                {t`Total operations`}:
              </span>
              <span className='font-bold tabular-nums text-foreground'>
                {formatUtils.number(month.count)}
              </span>
            </div>
          </div>
        </Card>
      ))}
    </Protected>
  );
}
